import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  ShieldCheck, ExternalLink, Edit3, Calendar, MessageSquare, Users,
  Home, LogOut, User, X, FileText, Settings,
} from 'lucide-react';
import { useMyAuth } from '../context/MyAuthContext';

const PORTAIL_URL = 'https://support.meteris.fr';

/* ── Actions contextuelles selon la route courante ─────────────────── */
function getContextualActions(pathname) {
  const actions = [];

  if (pathname.startsWith('/actualites') || pathname.startsWith('/news')) {
    actions.push({
      label: 'Gérer les actualités',
      icon: Edit3,
      href: '/my/actualites',
      external: false,
    });
    actions.push({
      label: 'Nouveau post (portail)',
      icon: FileText,
      href: `${PORTAIL_URL}/social`,
      external: true,
    });
  }
  if (pathname.startsWith('/prendre-rdv') || pathname.startsWith('/rdv')) {
    actions.push({
      label: 'Gérer les rendez-vous',
      icon: Calendar,
      href: `${PORTAIL_URL}/it-bookings`,
      external: true,
    });
  }
  if (pathname.startsWith('/contact')) {
    actions.push({
      label: 'Voir contacts reçus',
      icon: MessageSquare,
      href: `${PORTAIL_URL}/crm/contacts`,
      external: true,
    });
  }
  if (pathname.startsWith('/configurateur') || pathname.startsWith('/devis')) {
    actions.push({
      label: 'Voir les leads',
      icon: Users,
      href: `${PORTAIL_URL}/crm/leads`,
      external: true,
    });
  }

  return actions;
}

/* ── Composant principal ────────────────────────────────────────────── */
export default function AdminBar() {
  const { user, logout, loading } = useMyAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const containerRef = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) {
      document.addEventListener('mousedown', onMouseDown);
      document.addEventListener('keydown', onEsc);
      return () => {
        document.removeEventListener('mousedown', onMouseDown);
        document.removeEventListener('keydown', onEsc);
      };
    }
  }, [open]);

  // Ne rien afficher si pas superadmin (le check sera renforcé côté serveur de toute façon)
  if (loading || !user || user.role !== 'superadmin') return null;

  const actions = getContextualActions(location.pathname);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
      data-testid="admin-bar"
    >
      {open && (
        <div
          style={{
            position: 'absolute', bottom: 60, right: 0, minWidth: 270,
            background: '#0f172a', color: '#fff',
            borderRadius: 12, padding: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
            border: '1px solid #1e293b',
            animation: 'adminbar-in 0.15s ease-out',
          }}
        >
          <style>{`@keyframes adminbar-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

          <div style={{
            fontSize: 10, color: '#94a3b8',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            paddingBottom: 8, borderBottom: '1px solid #1e293b', marginBottom: 6,
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <ShieldCheck size={11} style={{ color: '#22c55e' }} />
              <span style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                Mode superadmin
              </span>
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 2 }}
            >
              <X size={13} />
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#cbd5e1', marginBottom: 8, paddingLeft: 2 }}>
            {user.email}
          </div>

          {actions.length > 0 && (
            <>
              <div style={sectionLabelStyle}>Sur cette page</div>
              {actions.map((a, i) => (
                <ActionLink key={i} {...a} />
              ))}
            </>
          )}

          <div style={sectionLabelStyle}>Raccourcis</div>
          <ActionLink label="Portail support" icon={Home} href={PORTAIL_URL} external />
          <ActionLink label="Mon profil" icon={User} href="/my/dashboard" />
          <ActionLink label="Admin meteris.fr" icon={Settings} href="/my/actualites" />

          <div style={{ borderTop: '1px solid #1e293b', marginTop: 8, paddingTop: 6 }}>
            <button
              onClick={async () => { await logout(); setOpen(false); }}
              style={{ ...menuBtnStyle, color: '#ef4444', width: '100%' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        title={open ? 'Fermer le menu superadmin' : 'Menu superadmin'}
        aria-label="Menu superadmin"
        style={{
          width: 44, height: 44, borderRadius: '50%',
          background: open ? '#0099D6' : 'rgba(15, 23, 42, 0.92)',
          color: '#fff',
          border: '2px solid rgba(255,255,255,0.1)',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          transition: 'background 0.2s, transform 0.2s',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'rgba(15, 23, 42, 1)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'rgba(15, 23, 42, 0.92)'; }}
      >
        <ShieldCheck size={18} />
      </button>
    </div>
  );
}

/* ── Styles partagés ────────────────────────────────────────────────── */
const menuBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '8px 10px', borderRadius: 6,
  background: 'transparent', border: 'none',
  color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
  textAlign: 'left', textDecoration: 'none',
  transition: 'background 0.12s',
};

const sectionLabelStyle = {
  fontSize: 9, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600,
  margin: '10px 0 2px', paddingLeft: 4,
};

function ActionLink({ label, icon: Icon, href, external }) {
  const handleMouseEnter = (e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)');
  const handleMouseLeave = (e) => (e.currentTarget.style.background = 'transparent');

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={menuBtnStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Icon size={13} />
        <span style={{ flex: 1 }}>{label}</span>
        <ExternalLink size={10} style={{ opacity: 0.55 }} />
      </a>
    );
  }
  return (
    <Link to={href} style={menuBtnStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Icon size={13} /> {label}
    </Link>
  );
}
